/**
 * Component test Email Service
 * Kiểm tra chức năng gửi email và lấy thông tin user
 */

import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { emailServicePHP } from '../../services/email/EmailServicePHP'
import toast from 'react-hot-toast'

const EmailServiceTest: React.FC = () => {
  const { user, isAuthenticated } = useAuth()
  const [isSending, setIsSending] = useState(false)
  const [lastResult, setLastResult] = useState<any>(null)

  // Debug logs
  console.log('🔍 EmailServiceTest - Auth state:', {
    user,
    isAuthenticated,
    hasUser: !!user,
    hasEmail: !!user?.email,
    hasName: !!user?.name
  })

  // Monitor auth state changes
  useEffect(() => {
    console.log('🔄 EmailServiceTest - Auth state changed:', {
      isAuthenticated,
      user: user ? {
        id: user.id,
        name: user.name,
        email: user.email,
        provider: user.provider
      } : null
    })
  }, [user, isAuthenticated])

  const testSendEmail = async () => {
    if (!user) {
      toast.error('Bạn cần đăng nhập để test email')
      return
    }

    setIsSending(true)
    console.log('🧪 Testing email service...')
    console.log('👤 User info:', {
      name: user.name,
      email: user.email,
      isAuthenticated
    })

    try {
      // Gửi email test
      const result = await emailServicePHP.notifyLessonCompletion(
        user.name || 'Test User',
        user.email,
        'Bài học test - Chuyển động thẳng biến đổi đều',
        'lop-10-chuong-1-bai-1-test',
        8, // correct answers
        10, // total questions
        300 // duration in seconds (5 minutes)
      )

      console.log('📧 Email result:', result)
      setLastResult(result)

      if (result.success) {
        toast.success('✅ Email test đã được gửi thành công!')
      } else {
        toast.error('❌ Gửi email thất bại: ' + result.message)
      }
    } catch (error: any) {
      console.error('❌ Test error:', error)
      toast.error('Lỗi: ' + error.message)
      setLastResult({ success: false, error: error.message })
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        🧪 Test Email Service
      </h2>

      {/* User Info */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
        <h3 className="font-bold text-blue-800 mb-2">👤 Thông tin đăng nhập hiện tại</h3>
        {isAuthenticated ? (
          <div className="space-y-2 text-sm">
            <p><strong>Tên:</strong> <span className="text-blue-600">{user?.name || 'N/A'}</span></p>
            <p><strong>Email:</strong> <span className="text-blue-600">{user?.email || 'N/A'}</span></p>
            <p><strong>Provider:</strong> <span className="text-blue-600">{user?.provider || 'N/A'}</span></p>
            <p><strong>Role:</strong> <span className="text-blue-600">{user?.role || 'N/A'}</span></p>
            {user?.email ? (
              <p className="text-green-600 font-semibold">✅ Email có sẵn - Sẽ gửi email cho user</p>
            ) : (
              <p className="text-orange-600 font-semibold">⚠️ Không có email - Chỉ gửi email cho admin</p>
            )}
          </div>
        ) : (
          <p className="text-red-600">❌ Chưa đăng nhập</p>
        )}
      </div>

      {/* Test Button */}
      <div className="mb-6">
        <button
          onClick={testSendEmail}
          disabled={isSending || !isAuthenticated}
          className={`w-full px-6 py-4 rounded-lg font-semibold text-white transition-all ${
            isSending
              ? 'bg-gray-400 cursor-not-allowed'
              : isAuthenticated
              ? 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {isSending ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Đang gửi email test...
            </span>
          ) : (
            '📧 Gửi Email Test'
          )}
        </button>
        {!isAuthenticated && (
          <p className="text-sm text-red-600 mt-2">⚠️ Bạn cần đăng nhập bằng Google để test email</p>
        )}
      </div>

      {/* Email Data Preview */}
      {isAuthenticated && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-bold text-gray-800 mb-3">📋 Dữ liệu email sẽ gửi</h3>
          <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
{JSON.stringify({
  studentName: user?.name || 'Test User',
  studentEmail: user?.email || 'N/A',
  lessonName: 'Bài học test - Chuyển động thẳng biến đổi đều',
  lessonId: 'lop-10-chuong-1-bai-1-test',
  score: '8/10',
  accuracy: '80',
  duration: '5 phút',
  completedAt: new Date().toLocaleString('vi-VN')
}, null, 2)}
          </pre>
        </div>
      )}

      {/* Result Display */}
      {lastResult && (
        <div className={`p-4 rounded-lg border-l-4 ${
          lastResult.success 
            ? 'bg-green-50 border-green-500' 
            : 'bg-red-50 border-red-500'
        }`}>
          <h3 className={`font-bold mb-2 ${lastResult.success ? 'text-green-800' : 'text-red-800'}`}>
            {lastResult.success ? '✅ Kết quả thành công' : '❌ Kết quả thất bại'}
          </h3>
          <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
{JSON.stringify(lastResult, null, 2)}
          </pre>

          {lastResult.success && lastResult.results && (
            <div className="mt-4 space-y-2">
              {lastResult.results.user && (
                <div className={`p-2 rounded ${lastResult.results.user.success ? 'bg-green-100' : 'bg-red-100'}`}>
                  <p className="text-sm font-semibold">
                    {lastResult.results.user.success ? '✅' : '❌'} Email User: {lastResult.results.user.recipient}
                  </p>
                  <p className="text-xs text-gray-600">{lastResult.results.user.message}</p>
                </div>
              )}
              {lastResult.results.admin && (
                <div className={`p-2 rounded ${lastResult.results.admin.success ? 'bg-green-100' : 'bg-red-100'}`}>
                  <p className="text-sm font-semibold">
                    {lastResult.results.admin.success ? '✅' : '❌'} Email Admin: {lastResult.results.admin.recipient}
                  </p>
                  <p className="text-xs text-gray-600">{lastResult.results.admin.message}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
        <h3 className="font-bold text-yellow-800 mb-2">💡 Hướng dẫn test</h3>
        <ol className="text-sm text-yellow-800 space-y-1 list-decimal list-inside">
          <li>Đăng nhập bằng Google (nếu chưa)</li>
          <li>Kiểm tra email hiển thị ở trên</li>
          <li>Click nút "Gửi Email Test"</li>
          <li>Kiểm tra console log</li>
          <li>Kiểm tra hộp thư email (có thể trong Spam)</li>
          <li>Admin luôn nhận email tại: quants82@gmail.com</li>
        </ol>
      </div>

      {/* API Status */}
      <div className="mt-6 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500">
        <h3 className="font-bold text-purple-800 mb-2">🔗 API Endpoint</h3>
        <p className="text-sm text-purple-800 font-mono">
          http://ebook.vatli365.vn/api/send-email.php
        </p>
        <p className="text-xs text-purple-600 mt-2">
          ⚠️ API phải được deploy trước khi test
        </p>
      </div>
    </div>
  )
}

export default EmailServiceTest

